import { Suspense, useContext } from "react";
import { useLocation } from "react-router-dom";

import { AuthContext } from "context/AuthContext";
import FullScreenLoader from "components/FullScreenLoader";
import NoDataState from "modules/swiftboard/components/NoDataState";
import { getLandingPath } from "utils/landing-path";

// Renders the right page for the logged-in user's role on a route that shows
// a different page per role (e.g. /swiftboard/home).
//
//   pages    — { ROLE_NAME: Component }. Declare them with React.lazy() at
//              module level so each user only downloads their own role's page.
//   fallback — page for any role not listed in `pages` (e.g. a role created
//              later). Without one, that role sees a "not set up" message.
//
// ModuleRouteGuard has already confirmed the route is assigned to the role,
// so a role without a page gets a message, never a 404.

function PageLoader() {
  return (
    <div className="h-full flex items-center justify-center bg-background">
      <div className="h-8 w-8 rounded-full border-2 border-border border-t-primary animate-spin" />
    </div>
  );
}

function RoleNotSetUp() {
  const { user, allowedPaths } = useContext(AuthContext);
  const { pathname } = useLocation();
  const landingPath = getLandingPath(user, allowedPaths);

  return (
    <div className="h-full overflow-y-auto bg-background">
      <NoDataState
        icon="alert"
        title="This page isn't set up for your role yet"
        message="This module is assigned to your role, but it doesn't have a page for your role yet. Please contact your administrator."
        showRefresh={false}
        showBack={landingPath !== pathname}
        backUrl={landingPath}
        actionText="Go to start page"
      />
    </div>
  );
}

export default function RoleSwitch({ pages, fallback }) {
  const { user, loading } = useContext(AuthContext);

  if (loading || !user) return <FullScreenLoader />;

  const Page = pages[user.user_role?.role] || fallback || RoleNotSetUp;

  return (
    <Suspense fallback={<PageLoader />}>
      <Page />
    </Suspense>
  );
}
